import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Link, 
  CreditCard, 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle,
  RefreshCw,
  Banknote
} from 'lucide-react';
import { TradingAccountsStats } from '@/services/admin/trading-accounts-service';

interface TradingAccountsStatsCardsProps {
  stats: TradingAccountsStats;
  isLoading?: boolean;
}

export function TradingAccountsStatsCards({ stats, isLoading }: TradingAccountsStatsCardsProps) {
  const formatBalance = (balance: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(balance);
  };

  const getStatusColor = (type: 'success' | 'warning' | 'error' | 'info') => {
    switch (type) {
      case 'success': return 'bg-green-500/10 text-green-500';
      case 'warning': return 'bg-yellow-500/10 text-yellow-500';
      case 'error': return 'bg-red-500/10 text-red-500';
      case 'info': return 'bg-blue-500/10 text-blue-500';
    }
  };

  const statsCards = [
    {
      title: 'Total Accounts',
      value: stats.total_accounts,
      icon: Link,
      description: 'API Connections',
      badge: { 
        text: `${stats.active_connections} active`, 
        type: 'success' as const 
      }
    },
    {
      title: 'Live Accounts',
      value: stats.live_accounts,
      icon: CreditCard,
      description: 'Live Trading',
      badge: { 
        text: `${stats.demo_accounts} demo`, 
        type: 'info' as const 
      }
    },
    {
      title: 'Total Balance',
      value: formatBalance(stats.total_balance),
      icon: Banknote,
      description: 'Across all accounts',
      badge: { 
        text: `${stats.demo_accounts} demo`, 
        type: 'info' as const 
      }
    },
    {
      title: 'Health Status',
      value: `${Math.round((stats.active_connections / Math.max(stats.active_connections + stats.inactive_connections, 1)) * 100)}%`,
      icon: TrendingUp,
      description: 'Connection Health',
      badge: { 
        text: stats.expired_connections > 0 ? `${stats.expired_connections} expired` : 'All good', 
        type: stats.expired_connections > 0 ? 'warning' as const : 'success' as const 
      }
    },
    {
      title: 'Recent Syncs',
      value: stats.recent_syncs,
      icon: RefreshCw,
      description: 'Last 24 hours',
      badge: { 
        text: stats.failed_syncs > 0 ? `${stats.failed_syncs} failed` : 'All synced', 
        type: stats.failed_syncs > 0 ? 'error' as const : 'success' as const 
      }
    },
    {
      title: 'Connection Issues',
      value: stats.inactive_connections + stats.failed_syncs,
      icon: AlertTriangle,
      description: 'Need attention',
      badge: { 
        text: stats.inactive_connections + stats.failed_syncs === 0 ? 'No issues' : 'Action needed', 
        type: stats.inactive_connections + stats.failed_syncs === 0 ? 'success' as const : 'error' as const 
      }
    }
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="h-4 bg-muted rounded w-24"></div>
              <div className="h-4 w-4 bg-muted rounded"></div>
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-muted rounded w-16 mb-2"></div>
              <div className="h-3 bg-muted rounded w-20"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {statsCards.map((card, index) => (
        <Card key={index} className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {card.title}
            </CardTitle>
            <card.icon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground mb-1">
              {card.value}
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {card.description}
              </p>
              <Badge 
                variant="secondary" 
                className={getStatusColor(card.badge.type)}
              >
                {card.badge.text}
              </Badge>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};